<script lang="ts">
	import { get } from 'svelte/store';
	//Formatting
	import { format } from 'svelte-i18n';
	//Design system components
	import { CardGrid, Input, SelectCard, Text, Token } from '@Shelly/shelly-design-system';
	//Helpers
	import { capFirstChar, getErrorOrInfoMessageForMinAndMaxUnit } from 'src/helpers/utilities';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import {
		CAMERA_DURATION_MAX,
		CAMERA_DURATION_MIN,
		CAMERA_LOCAL_ACTION_METHODS,
		getCameraConfigActionTitle,
		getCameraLocalActionCards,
		getCameraLocalActionZones,
		getCameraStateCards,
		getSelectedCameraAction,
		isCameraConfigAction,
		type CameraLocalAction,
	} from 'src/helpers/actions/local/devices/camera_local_actions';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Types
	import type { Device } from 'src/types/Device';
	import type { LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';

	export let controlOption: LocalControlOptions;
	export let controlOptions: LocalControlOptions[] = [];
	export let device: Device;
	export let method = '';

	const zoneOptions = getCameraLocalActionZones(device);
	const actions = getCameraLocalActionCards(zoneOptions.length > 0);

	const CAMERA_DURATION_UNIT = get(format)('select_units_seconds', {
		default: 'seconds',
	});

	const onActionSelect = (action: CameraLocalAction) => {
		selectedAction = action;
		stateOptions = getCameraStateCards(selectedAction);
		method = CAMERA_LOCAL_ACTION_METHODS[action];
		controlOption = controlOptions.find((option) => option.value === method);
		if (isCameraConfigAction(action)) {
			controlOption.attr.config ??= {};
			controlOption.params.config ??= {};
			controlOption.attr.config.privacy = false;
			controlOption.attr.config.arm = false;
		} else if (action === 'zone') {
			selectedZone = '';
			controlOption.attr.config ??= {};
			controlOption.params.config ??= {};
			controlOption.attr.config.enable = false;
			controlOption.params.config.enable = false;
		}
	};

	const onConfigStateSelect = (value: boolean) => {
		const configKey = selectedAction === 'zone' ? 'enable' : selectedAction;
		controlOption.attr.config ??= {};
		controlOption.params.config ??= {};
		controlOption.attr.config[configKey] = true;
		controlOption.params.config[configKey] = value;
		controlOption = controlOption;
	};

	const onZoneSelect = (zoneId: string) => {
		selectedZone = zoneId;
		controlOption.params.id = Number(zoneId);
		controlOption.attr.config ??= {};
		controlOption.params.config ??= {};
		controlOption.attr.config.enable = false;
		controlOption.params.config.enable = false;
		controlOption = controlOption;
	};

	const setDuration = (duration: string) => {
		if (duration === '') {
			controlOption.attr.duration = false;
			controlOption.params.duration = null;
			return;
		}
		controlOption.attr.duration = true;
		controlOption.params.duration = Number(duration);
	};

	let selectedAction = getSelectedCameraAction(method);
	if (selectedAction === 'privacy' && controlOption?.attr?.config?.arm) {
		selectedAction = 'arm';
	}
	let selectedZone = selectedAction === 'zone' ? String(controlOption.params.id ?? '') : '';
	let stateOptions = getCameraStateCards(selectedAction);

	$: minMaxInfo = getErrorOrInfoMessageForMinAndMaxUnit(
		controlOption.params.duration,
		CAMERA_DURATION_MIN,
		CAMERA_DURATION_MAX,
		CAMERA_DURATION_UNIT,
	);
</script>

<Text element="h2" class="mb-s">
	{capFirstChar($format('label_select_action', { default: 'Select action' }))}
</Text>
<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
	{#each actions as action}
		<div class="d-flex">
			<SelectCard
				cardBgColor={$showNewLayout ? 'color-1' : undefined}
				class="width-full"
				type="horizontal"
				variant="radio"
				title={capFirstChar(action.title)}
				icon={action.icon}
				selected={selectedAction === action.id}
				on:click={() => onActionSelect(action.id)}
			/>
		</div>
	{/each}
</CardGrid>

{#if selectedAction === 'record'}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{$format('scene_camera_record_duration', { default: 'Recording duration (optional)' })}
		</Text>
		<Input
			size="m"
			value={String(controlOption.params.duration ?? '')}
			label={$format('local_action_camera_duration_placeholder', {
				default: 'e.g. 30 (in seconds)',
			})}
			{...getNumberInputConfig()}
			icon="fa fa-timer"
			tabIndex="-1"
			info={minMaxInfo.info}
			error={controlOption.attr.duration && minMaxInfo.error}
			id="input-camera-duration"
			aria-labelledby="input-camera-duration"
			on:input={(event) => setDuration(event.target['value'].trim())}
		/>
	</div>
{:else if selectedAction === 'zone'}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{$format('select_zone', { default: 'Select Zone' })}
		</Text>
		<div class="d-flex flex-wrap mb-l gap-s">
			{#each zoneOptions as zone}
				<Token
					id="token-local-camera-zone-{zone.id}"
					title={capFirstChar(zone.title)}
					icon={zone.icon}
					size="m"
					selected={selectedZone === zone.id}
					on:click={() => onZoneSelect(zone.id)}
				/>
			{/each}
		</div>

		{#if selectedZone}
			<Text element="h2" class="mb-s">
				{capFirstChar(
					$format('scene_camera_select_zone_state', {
						values: {
							zone:
								zoneOptions.find((zone) => zone.id === selectedZone)?.title ||
								`Zone ${selectedZone}`,
						},
						default: `Change zone ${selectedZone} to`,
					}),
				)}
			</Text>
			<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
				{#each stateOptions as option}
					<div class="d-flex">
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							class="width-full"
							type="horizontal"
							variant="radio"
							title={capFirstChar(option.title)}
							icon={option.icon}
							selected={controlOption.attr.config?.enable &&
								controlOption.params.config?.enable === option.value}
							on:click={() => onConfigStateSelect(option.value)}
						/>
					</div>
				{/each}
			</CardGrid>
		{/if}
	</div>
{:else if isCameraConfigAction(selectedAction)}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{getCameraConfigActionTitle(selectedAction)}
		</Text>
		<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
			{#each stateOptions as option}
				<div class="d-flex">
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						class="width-full"
						type="horizontal"
						variant="radio"
						title={capFirstChar(option.title)}
						icon={option.icon}
						selected={controlOption.attr.config?.[selectedAction] &&
							controlOption.params.config?.[selectedAction] === option.value}
						on:click={() => onConfigStateSelect(option.value)}
					/>
				</div>
			{/each}
		</CardGrid>
	</div>
{/if}
